import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useBulkUploadDocuments } from '@/hooks/api'
import {
  MAX_FILE_SIZE,
  ALLOWED_MIME_TYPES,
  DEFAULT_DOCUMENT_TYPE,
} from './UploadDocumentDialog.static'
import type { FileItem } from './UploadDocumentDialog.static'
import type { ApiError } from '@/types'

interface UseUploadDocumentDialogOptions {
  onClose: () => void
  patientId?: string
  staffId?: string
}

export function useUploadDocumentDialog({
  onClose,
  patientId,
  staffId,
}: UseUploadDocumentDialogOptions) {
  const { t } = useTranslation('documents')
  const [fileItems, setFileItems] = useState<FileItem[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)

  const bulkUploadMutation = useBulkUploadDocuments()
  const isLoading = bulkUploadMutation.isPending

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const errors: string[] = []
      const valid: FileItem[] = []

      for (const file of acceptedFiles) {
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`${file.name}: ${t('errors.fileTooLarge')}`)
          continue
        }
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
          errors.push(`${file.name}: ${t('errors.invalidFileType')}`)
          continue
        }
        valid.push({ file, documentType: DEFAULT_DOCUMENT_TYPE, description: '', documentDate: '' })
      }

      setFileErrors(errors)
      setFileItems((prev) => {
        const existingNames = new Set(prev.map((item) => item.file.name))
        return [...prev, ...valid.filter((item) => !existingNames.has(item.file.name))]
      })
    },
    [t]
  )

  const removeFile = useCallback((index: number) => {
    setFileItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const updateFileItem = useCallback((index: number, patch: Partial<Omit<FileItem, 'file'>>) => {
    setFileItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }, [])

  const handleSubmit = () => {
    if (fileItems.length === 0) {
      setFileErrors([t('errors.fileRequired')])
      return
    }

    setSubmitError(null)
    bulkUploadMutation.mutate(
      {
        files: fileItems.map((item) => item.file),
        documentTypes: fileItems.map((item) => item.documentType),
        descriptions: fileItems.map((item) => item.description),
        documentDates: fileItems.map((item) => item.documentDate),
        patientId,
        staffId,
      },
      {
        onSuccess: () => {
          setFileItems([])
          setFileErrors([])
          onClose()
        },
        onError: (error) => {
          const axiosError = error as AxiosError<ApiError>
          setSubmitError(axiosError.response?.data?.error?.message || t('uploadFailed'))
        },
      }
    )
  }

  return {
    t,
    fileItems,
    fileErrors,
    submitError,
    isLoading,
    onDrop,
    removeFile,
    updateFileItem,
    handleSubmit,
  }
}
